
import Vue from 'vue';
import { mapState, mapActions, mapGetters } from 'vuex';
import { debounce } from 'lodash';
import { db } from '@/firebase';
import { SURVEY_KEY, NewUserOnboardingSurveyFields, Survey } from '@run-diffusion/shared';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { emailMatchesFallbackEnterpriseDomain } from '@/utils/enterpriseFallbackDomains';
import { analytics, EVENTS } from '@/services/analytics';
import DialogOrBottomSheet from '@/components/base/DialogOrBottomSheet.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import RunnitMarkColorLogo from '@/assets/RunnitMarkColorLogo.vue';

type QuestionKind = 'SINGLE_SELECT' | 'MULTI_SELECT' | 'TEXT_INPUT';
type SelectItem = { label: string, value: string };
type MqlCriteriaRule = {
	key: string,
	qualifyingValues: string[],
};

export default Vue.extend({
	name: 'NewUserOnboardingDialog',
	props: {
		value: { type: Boolean, default: false },
		openToCalendar: { type: Boolean, default: false },
		// the Boards v2 tour follows the survey (IL-095): a submitted survey goes
		// straight on to it (a lead sees the sales calendar first), no thank-you step
		tourNext: { type: Boolean, default: false },
	},
	data () {
		return {
			windowStep: this.openToCalendar ? 1 : 0,
			submitting: false,
			surveyId: null as string | null,
			debouncedAutoSave: null as any,
			openMultiSelectKey: null as string | null,
			multiSelectScrollTimeout: null as ReturnType<typeof setTimeout> | null,
			fetchingSurvey: true,
			hubspotMeetingEmbedUrl: 'https://meetings.hubspot.com/blake-gibbons/demo-product?uuid=10434dd7-12cc-4183-b89f-fda0a19a3a17&embed=true&embedType=iframe&width=840&height=700',
			mqlQualifyingCriteria: [
				{
					key: 'teamSize',
					qualifyingValues: ['51-200', '201-500', '501-1000', '1001-5000', '5001-10000', '10000+'],
				},
				{
					key: 'role',
					qualifyingValues: ['Designer', 'Creative Director', 'Developer / Technical', 'Product Manager', 'Marketer'],
				},
			] as MqlCriteriaRule[],

			answers: {
				role: null,
				roleOther: '',

				hearAboutUs: null,
				hearAboutUsOther: '',

			experienceLevel: null,

			industry: null,
				industryOther: '',

				teamSize: null,
				companyName: '',
				hearAboutUsSocial: null,
				hearAboutUsAI: null,
			} as Record<string, any>,

			questions: [
				{
					key: 'role',
					kind: 'SINGLE_SELECT' as QuestionKind,
					label: 'What best describes your role?',
					items: [
						{ label: 'Designer', value: 'Designer' },
						{ label: 'Creative Director', value: 'Creative Director' },
						{ label: 'Developer / Technical', value: 'Developer / Technical' },
						{ label: 'Product Manager', value: 'Product Manager' },
						{ label: 'Marketer', value: 'Marketer' },
						{ label: 'Student / Hobbyist', value: 'Student / Hobbyist' },
						{ label: 'Consultant', value: 'Consultant' },
						{ label: 'Hobbyist', value: 'Hobbyist' },
						{ label: 'Other', value: 'Other' },
					] as SelectItem[],
					otherKey: 'roleOther',
				},
				{
					key: 'industry',
					kind: 'SINGLE_SELECT' as QuestionKind,
					label: 'What industry do you work in or create for?',
					items: [
						{ label: 'Advertising / Marketing', value: 'Advertising / Marketing' },
						{ label: 'Architecture', value: 'Architecture' },
						{ label: 'Game Development', value: 'Game Development' },
						{ label: 'Interior Design', value: 'Interior Design' },
						{ label: 'Fashion', value: 'Fashion' },
						{ label: 'Video (Film / Animation / VFX)', value: 'Video Production' },
						{ label: 'Education', value: 'Education' },
						{ label: 'Agency', value: 'Agency' },
						{ label: 'Freelance', value: 'Freelance' },
						{ label: 'Consultant', value: 'Consultant' },
						{ label: 'Other', value: 'Other' },
					] as SelectItem[],
					otherKey: 'industryOther',
				},
				{
					key: 'experienceLevel',
					kind: 'SINGLE_SELECT' as QuestionKind,
					label: 'How would you describe your experience level with generative AI tools?',
					items: [
						{ label: 'Beginner (new to AI tools)', value: 'Beginner' },
						{ label: 'Intermediate (used a few tools)', value: 'Intermediate' },
						{ label: 'Advanced (power user)', value: 'Advanced' },
						{ label: 'Expert / Developer', value: 'Expert' },
					] as SelectItem[],
				},
				{
					key: 'teamSize',
					kind: 'SINGLE_SELECT' as QuestionKind,
					label: 'How many people are in your company?',
					items: [
						{ label: '1', value: '1' },
						{ label: '2-10', value: '2-10' },
						{ label: '11-50', value: '11-50' },
						{ label: '51-200', value: '51-200' },
						{ label: '201-500', value: '201-500' },
						{ label: '501-1000', value: '501-1000' },
						{ label: '1001-5000', value: '1001-5000' },
						{ label: '5001-10000', value: '5001-10000' },
						{ label: '10000+', value: '10000+' },
					] as SelectItem[],
				},
				{
					key: 'companyName',
					kind: 'TEXT_INPUT' as QuestionKind,
					label: 'What is your company name?',
				},
				{
					key: 'hearAboutUs',
					kind: 'SINGLE_SELECT' as QuestionKind,
					label: 'How did you hear about us?',
					items: [
						{ label: 'Referral', value: 'Referral' },
						{ label: 'Social Media', value: 'Social Media' },
						{ label: 'Search (Google)', value: 'Search' },
						{ label: 'AI Chat (ChatGPT, Gemini)', value: 'AI Chat' },
						{ label: 'Ad', value: 'Ad' },
						{ label: 'Event / Newsletter', value: 'Event / Newsletter' },
						{ label: 'Other', value: 'Other' },
					] as SelectItem[],
					otherKey: 'hearAboutUsOther',
				},
			],
			hearAboutUsSocialOptions: [
				{ label: 'Facebook', value: 'Facebook' },
				{ label: 'Instagram', value: 'Instagram' },
				{ label: 'TikTok', value: 'TikTok' },
				{ label: 'YouTube', value: 'YouTube' },
				{ label: 'X (Twitter)', value: 'X (Twitter)' },
				{ label: 'Discord', value: 'Discord' },
				{ label: 'LinkedIn', value: 'LinkedIn' },
				{ label: 'Other', value: 'Other' },
			],
			hearAboutUsAIOptions: [
				{ label: 'ChatGPT', value: 'ChatGPT' },
				{ label: 'Google Gemini', value: 'Gemini' },
				{ label: 'Claude', value: 'Claude' },
				{ label: 'Perplexity AI', value: 'Perplexity' },
				{ label: 'Grok', value: 'Grok' },
				{ label: 'Other', value: 'Other' },
			],
		};
	},
	computed: {
		...mapState([
			'user',
			'team',
		]),
		...mapGetters([
			'skipNewUserSurvey',
		]),
		canSkipOnboarding () {
			const email = (this.user?.email ?? '').trim().toLowerCase();
			return !!(this.user && (
				this.user.isAdmin
				|| (email && email.endsWith('@rundiffusion.com'))
			));
		},
		visibleQuestions () {
			return this.questions
				.map((q, index) => ({ q, index }))
				.filter(({ index }) => this.isVisible(index));
		},
		submitDisabled () {
			if (this.submitting) return true;
			return this.questions.some((_, index) => this.isVisible(index) && !this.isQuestionComplete(index));
		},
		showFooterShadow () {
			// Show shadow after 4th question (index 3) appears
			return this.isVisible(3);
		},
		dialogMaxWidth () {
			return this.windowStep === 1 ? 960 : 620;
		},
		dialogContentClass () {
			return this.windowStep === 1
				? 'onboarding-bottom-sheet-content onboarding-bottom-sheet-content-calendar'
				: 'onboarding-bottom-sheet-content';
		},
		companyNameRequired () {
			const LARGE_TEAM_SIZES = ['51-200', '201-500', '501-1000', '1001-5000', '5001-10000', '10000+'];
			return LARGE_TEAM_SIZES.includes(this.answers.teamSize);
		},
		isCalendarBlacklisted () {
			// Don't pitch the sales calendar to existing enterprise clients. Primary signal is the
			// data-driven skipNewUserSurvey (verified domain claim of an Enterprise-tier company);
			// the shared fallback domain list is the additive client-side shim (see
			// enterpriseFallbackDomains) for clients not yet covered by a verified domain claim.
			return !!(this.skipNewUserSurvey || emailMatchesFallbackEnterpriseDomain(this.user?.email));
		},
		isMQL () {
			if (this.isCalendarBlacklisted) return false;
			return this.mqlQualifyingCriteria.every((rule) => {
				const answer = this.answers[rule.key];
				if (!answer) return false;
				return rule.qualifyingValues.includes(answer);
			});
		},
		hasAnyAnswer () {
			return Object.values(this.answers).some((val) => {
				if (Array.isArray(val)) return val.length > 0;
				return !!val;
			});
		},
	},
	async created () {
		this.fetchingSurvey = true;
		try {
			this.debouncedAutoSave = debounce(this.autoSave, 2000);
			await this.fetchExistingSurvey();
		} catch (e) {
			console.error('[NewUserOnboardingDialog] Error in created method:', e);
		} finally {
			this.fetchingSurvey = false;
		}
	},
	watch: {
		value (newVal: boolean) {
			if (!newVal) return;
			this.windowStep = this.openToCalendar ? 1 : 0;
		},
		answers: {
			handler (newVal, oldVal) {
				// Skip if we are just initializing (oldVal would be empty or initial state)
				const hasAnyAnswer = Object.values(newVal).some((val) => {
					if (Array.isArray(val)) return val.length > 0;
					return !!val;
				});

				// Clear company name if role is Hobbyist
				if (newVal.role === 'Hobbyist' && newVal.companyName) {
					this.answers.companyName = '';
				}

				if (this.debouncedAutoSave) {
					this.debouncedAutoSave();
				}
			},
			deep: true,
		},
		visibleQuestions: {
			handler (newVal, oldVal) {
				// When a new question appears, scroll it into view
				// But skip if a multi-select menu is currently open
				if (newVal && oldVal && newVal.length > oldVal.length && !this.openMultiSelectKey) {
					this.$nextTick(() => {
						this.scrollToNewQuestion(newVal[newVal.length - 1]);
					});
				}
			},
			immediate: false,
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		onDialogInput (val: boolean) {
			// Only allow closing if we're on a non-survey step
			if (this.windowStep >= 1) {
				this.$emit('input', val);
			}
		},
		onClose () {
			this.$emit('input', false);
		},
		onSkip () {
			this.$emit('input', false);
		},
		onMultiSelectInput (key: string, value: any) {
			const previousVisibleCount = this.visibleQuestions.length;
			this.answers = { ...this.answers, [key]: value };
			// Mark this multi-select as open
			this.openMultiSelectKey = key;

			// Set a timeout to check for scroll after menu likely closes
			// Clear any existing timeout
			if (this.multiSelectScrollTimeout) {
				clearTimeout(this.multiSelectScrollTimeout);
			}
			this.multiSelectScrollTimeout = setTimeout(() => {
				this.checkAndScrollToNewQuestion(previousVisibleCount);
			}, 300);
		},
		onMultiSelectMenuChange (key: string, searchInput: any) {
			// When search input becomes null/empty, menu is likely closing
			if (searchInput === null || searchInput === '') {
				this.openMultiSelectKey = null;
			}
		},
		checkAndScrollToNewQuestion (previousVisibleCount: number) {
			this.openMultiSelectKey = null;
			const currentVisibleQuestions = this.visibleQuestions;
			if (currentVisibleQuestions.length > previousVisibleCount) {
				this.$nextTick(() => {
					this.scrollToNewQuestion(currentVisibleQuestions[currentVisibleQuestions.length - 1]);
				});
			}
		},
		includesOther (key: string) {
			const val = this.answers[key];
			if (Array.isArray(val)) return val.includes('Other');
			return val === 'Other';
		},
		isVisible (index: number) {
			const q = this.questions[index];

			if (q.key === 'companyName') {
				if (!this.answers.role || this.answers.role === 'Hobbyist') return false;
			}

			// First 3 questions visible; then reveal N+1 once N is complete (starting from question #3).
			if (index <= 2) return true;

			// Walk backwards to find the previous non-skipped question
			let prev = index - 1;
			while (prev >= 0 && !this.isVisible(prev)) {
				prev--;
			}
			if (prev < 0) return true;
			return this.isQuestionComplete(prev);
		},
		isQuestionComplete (index: number) {
			const q = this.questions[index];
			const val = this.answers[q.key];

			if (q.key === 'companyName') {
				if (!this.companyNameRequired) return true;
				return !!(this.answers.companyName && String(this.answers.companyName).trim());
			}

			const hasValue = Array.isArray(val)
				? val.length > 0
				: !!val;

			if (!hasValue) return false;

			// Check for "Other" text field
			if (this.includesOther(q.key) && q.otherKey) {
				return !!(this.answers[q.otherKey] && String(this.answers[q.otherKey]).trim().length);
			}

			// Special handling for "hearAboutUs" question with sub-questions
			if (q.key === 'hearAboutUs') {
				if (val === 'Social Media') {
					// Must also answer the social media platform sub-question
					return !!this.answers.hearAboutUsSocial;
				}
				if (val === 'AI Chat (ChatGPT, Gemini)') {
					// Must also answer the AI chat provider sub-question
					return !!this.answers.hearAboutUsAI;
				}
			}

			return true;
		},
		scrollToNewQuestion (questionItem: { q: any, index: number }) {
			const scrollContainer = this.$refs.questionsScroll as HTMLElement;
			if (!scrollContainer) return;

			const questionElement = scrollContainer.querySelector(`[data-question-key="${questionItem.q.key}"]`) as HTMLElement;
			if (!questionElement) return;

			const containerRect = scrollContainer.getBoundingClientRect();
			const questionRect = questionElement.getBoundingClientRect();

			// Calculate scroll position to bring question into view
			const scrollTop = scrollContainer.scrollTop;
			const questionTop = questionRect.top - containerRect.top + scrollTop;
			const questionBottom = questionRect.bottom - containerRect.top + scrollTop;
			const containerHeight = scrollContainer.clientHeight;

			// If question is below visible area, scroll to show it
			if (questionBottom > scrollTop + containerHeight) {
				scrollContainer.scrollTo({
					top: questionTop - 20, // Add some padding from top
					behavior: 'smooth',
				});
			}
		},
		formatOtherValue (val: string, otherText: string) {
			if (val !== 'Other') return val;
			return `Other: ${String(otherText || '').trim()}`;
		},
		formatOtherArray (vals: string[], otherText: string) {
			return (vals || []).map((v) => {
				if (v !== 'Other') return v;
				return `Other: ${String(otherText || '').trim()}`;
			});
		},
		getSurveyData (completed = false): Partial<Survey<NewUserOnboardingSurveyFields>> {
			const teamId: string = this._get(this.team, 'id', null);

			return {
				key: SURVEY_KEY.NEW_USER_ONBOARDING_1,
				userId: this.user.id,
				...(teamId && { teamId }),
				completed,
				answers: this.answers, // Store raw answers for resumption
				fields: {
					role: {
						label: 'What best describes your role?',
						type: 'TEXT',
						value: this.formatOtherValue(this.answers.role, this.answers.roleOther),
					},
					hearAboutUs: {
						label: 'How did you hear about us?',
						type: 'TEXT',
						value: this.formatOtherValue(this.answers.hearAboutUs, this.answers.hearAboutUsOther),
					},
					...(this.answers.hearAboutUsSocial && {
						hearAboutUsSocial: {
							label: 'Which social media platform?',
							type: 'TEXT',
							value: this.answers.hearAboutUsSocial,
						},
					}),
					...(this.answers.hearAboutUsAI && {
						hearAboutUsAI: {
							label: 'Which AI chat provider?',
							type: 'TEXT',
							value: this.answers.hearAboutUsAI,
						},
					}),
					experienceLevel: {
						label: 'How would you describe your experience level with creative AI tools?',
						type: 'TEXT',
						value: this.answers.experienceLevel,
					},
					industry: {
						label: 'What industry do you work in or create for?',
						type: 'TEXT',
						value: this.formatOtherValue(this.answers.industry, this.answers.industryOther),
					},
					teamSize: {
						label: 'How many people are on your team?',
						type: 'TEXT',
						value: this.answers.teamSize,
					},
					...(this.answers.companyName && String(this.answers.companyName).trim() && {
						companyName: {
							label: 'Company name',
							type: 'TEXT',
							value: String(this.answers.companyName).trim(),
						},
					}),
				},
			};
		},
		async fetchExistingSurvey () {
			if (!this.user || !this.user.id) {
				return;
			}

			try {
				const snap = await db
					.collection('surveys')
					.where('userId', '==', this.user.id)
					.where('key', '==', SURVEY_KEY.NEW_USER_ONBOARDING_1)
					.where('completed', '==', false)
					.limit(1)
					.get();

				if (!snap.empty) {
					const doc = snap.docs[0];
					const data = doc.data();
					if (data && !data.completed) {
						this.surveyId = doc.id;
						if (data.answers) {
							this.answers = { ...this.answers, ...data.answers };
						}
					}
				}
			} catch (e) {
				console.error('[NewUserOnboardingDialog] Error fetching existing onboarding survey:', e);
			}
		},
		async autoSave () {
			// Don't auto-save if already submitting, still fetching, or if no answers yet
			if (this.submitting || this.fetchingSurvey) {
				return;
			}
			// Don't auto-save once user has moved past the survey form step.
			if (this.windowStep >= 1) {
				return;
			}
			if (!this.hasAnyAnswer) {
				return;
			}

			try {
				const data = this.getSurveyData(false);

				if (!this.surveyId) {
					const newDoc = db.collection('surveys').doc();
					this.surveyId = newDoc.id;
					await newDoc.set({
						...data,
						createdAt: new Date(),
						updatedAt: new Date(),
					});
				} else {
					await db.doc(`surveys/${this.surveyId}`).set({
						...data,
						updatedAt: new Date(),
					}, { merge: true });
				}
			} catch (e) {
				console.error('[NewUserOnboardingDialog] autoSave: Error auto-saving onboarding survey:', e);
			}
		},
		async onSubmit () {
			if (this.submitDisabled) {
				return;
			}

			// Cancel any queued debounced autosave so it can't overwrite completed:true.
			if (this.debouncedAutoSave?.cancel) {
				this.debouncedAutoSave.cancel();
			}

			this.submitting = true;
			let handingOff = false;
			try {
				const data = this.getSurveyData(true);

				if (!this.surveyId) {
					const newDoc = db.collection('surveys').doc();
					this.surveyId = newDoc.id;
					await newDoc.set({
						...data,
						createdAt: new Date(),
						updatedAt: new Date(),
					});
				} else {
					await db.doc(`surveys/${this.surveyId}`).set({
						...data,
						updatedAt: new Date(),
					}, { merge: true });
				}

				analytics.track(EVENTS.ONBOARDING_COMPLETED, {
					role: this._get(this.answers, 'role', null),
					industry: this._get(this.answers, 'industry', null),
					experience_level: this._get(this.answers, 'experienceLevel', null),
					team_size: this._get(this.answers, 'teamSize', null),
					hear_about_us: this._get(this.answers, 'hearAboutUs', null),
					is_mql: !!this.isMQL,
				});

				// Show success snackbar and switch to thank you window (with the tour
				// next, the tour's welcome says it instead, unobstructed)
				if (!this.tourNext || this.isMQL) {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.SUCCESS,
						message: 'Thanks for your responses!',
						show: true,
					});
				}
				if (this.isMQL) {
					await db.doc(`users/${this.user.id}`).update({
						'gtm.isMql': true,
					});
					this.windowStep = 1;
				} else if (!this.tourNext) {
					this.windowStep = 2;
				}
				// the host carries on: with the tour next, it takes the person there (Submit stays busy till then)
				handingOff = this.tourNext && !this.isMQL;
				this.$emit('completed', { isMql: !!this.isMQL });
			} catch (e) {
				console.error('Error submitting onboarding survey:', e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Something went wrong. Please try again.',
					show: true,
				});
			} finally {
				if (!handingOff) this.submitting = false;
			}
		},
	},
	components: {
		BaseButton,
		DialogOrBottomSheet,
		RunnitMarkColorLogo,
	},
});
